import { Link } from 'react-router-dom';
import { TrendingDown, AlertCircle, CheckSquare, LineChart, ChevronRight } from 'lucide-react';
import BalanceCard from '../components/BalanceCard.jsx';
import KpiCard from '../components/KpiCard.jsx';
import AlertBanner from '../components/AlertBanner.jsx';
import PeriodSwitcher from '../components/PeriodSwitcher.jsx';
import MonthlyChart from '../components/MonthlyChart.jsx';
import ReceiptCaptureButton from '../components/ReceiptCaptureButton.jsx';
import PendingReceiptsBanner from '../components/PendingReceiptsBanner.jsx';
import MonthBreakEvenWidget from '../components/MonthBreakEvenWidget.jsx';
import { useCategories } from '../hooks/useCategories.js';
import { useStatsMonthly } from '../hooks/useStatsMonthly.js';
import { useCashFlow } from '../hooks/useCashFlow.js';
import { usePendingRecurrencesCount } from '../hooks/useRecurrences.js';
import { useUnclassifiedCount } from '../hooks/useTransactions.js';
import { useEntityScope } from '../hooks/useEntityScope.js';
import { formatCurrency } from '../lib/format.js';
import { colorForVariance } from '../lib/variance.js';

const COLOR_MAP = {
  green: 'text-green-600',
  orange: 'text-orange-500',
  red: 'text-red-600',
};

export default function HomePage() {
  const currentYear = new Date().getFullYear();
  const { data: catData, isLoading: catLoading } = useCategories();
  const { data: statsData } = useStatsMonthly(currentYear);
  const { data: pendingCount } = usePendingRecurrencesCount();
  const { data: unclassifiedCount } = useUnclassifiedCount();
  const { activeEntityName } = useEntityScope();

  // Cash-flow preview: next 12 months, monthly
  const today = new Date();
  const cfFrom = today.toISOString().slice(0, 10);
  const cfTo = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
    .toISOString()
    .slice(0, 10);
  const { data: cfData } = useCashFlow({ from: cfFrom, to: cfTo, granularity: 'month' });

  const categories = Array.isArray(catData)
    ? catData
    : catData?.categories ?? catData?.data ?? [];

  // Total realized + budget this month
  const totalRealized = categories.reduce((s, c) => s + (c.total_month_cents ?? 0), 0);
  const totalBudget = categories.reduce((s, c) => s + (c.budget_month_cents ?? 0), 0);

  // Overdraft categories > 15%
  const overdraftCats = categories.filter((c) => {
    if (!c.budget_month_cents) return false;
    return colorForVariance(c.total_month_cents ?? 0, c.budget_month_cents) === 'red';
  });

  // Cash-flow projected net M+12
  const cfFutureBuckets = (cfData?.buckets ?? []).filter((b) => b.is_future);
  const cfProjectedNet = cfFutureBuckets.reduce(
    (sum, b) => sum + (b.forecast?.net_cents ?? b.actual?.net_cents ?? 0),
    0
  );

  // YTD ecart
  const ytdRealized = (statsData ?? []).reduce(
    (s, row) => s + (row.by_category ?? []).reduce((ss, c) => ss + (c.realized_cents ?? 0), 0),
    0
  );
  const ytdBudget = (statsData ?? []).reduce(
    (s, row) => s + (row.by_category ?? []).reduce((ss, c) => ss + (c.budget_cents ?? 0), 0),
    0
  );
  const ytdEcart = ytdBudget > 0 ? ((ytdRealized - ytdBudget) / ytdBudget) * 100 : null;
  const ytdColor = ytdEcart !== null ? COLOR_MAP[colorForVariance(ytdRealized, ytdBudget)] : '';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Tableau de bord</h1>
        <p className="text-sm text-slate-500">
          Suivi financier {activeEntityName ?? '…'}
        </p>
      </div>

      <ReceiptCaptureButton variant="hero" />
      <PendingReceiptsBanner />

      {/* Alertes */}
      <div className="space-y-2">
        {(pendingCount ?? 0) > 0 && (
          <AlertBanner
            type="warning"
            message={`${pendingCount} prévisionnel${pendingCount > 1 ? 's' : ''} en attente de validation`}
            href="/previsionnels#pending"
          />
        )}
        {(unclassifiedCount ?? 0) > 0 && (
          <AlertBanner
            type="info"
            message={`${unclassifiedCount} transaction${unclassifiedCount > 1 ? 's' : ''} non classifiée${unclassifiedCount > 1 ? 's' : ''}`}
            href="/depenses/transactions?unclassified=true"
          />
        )}
        {overdraftCats.map((cat) => (
          <AlertBanner
            key={cat.id}
            type="error"
            message={`Dépassement budget > 15 % — ${cat.name}`}
            href="/depenses"
          />
        ))}
      </div>

      {/* Solde Qonto */}
      <BalanceCard />

      {/* Point mort du mois */}
      <MonthBreakEvenWidget />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          label="Dépenses du mois"
          value={formatCurrency(totalRealized)}
          sub={totalBudget > 0 ? `Budget : ${formatCurrency(totalBudget)}` : undefined}
          icon={TrendingDown}
          loading={catLoading}
          accentClass={totalBudget > 0 ? COLOR_MAP[colorForVariance(totalRealized, totalBudget)] : ''}
        />
        <KpiCard
          label="Écart YTD"
          value={ytdEcart !== null ? `${ytdEcart > 0 ? '+' : ''}${ytdEcart.toFixed(1)} %` : '—'}
          sub="vs budget année en cours"
          icon={AlertCircle}
          accentClass={ytdColor}
        />
        <KpiCard
          label="Catégories en dépassement"
          value={String(overdraftCats.length)}
          sub="Seuil > 15 % du budget"
          icon={CheckSquare}
          accentClass={overdraftCats.length > 0 ? 'text-red-600' : 'text-green-600'}
        />
      </div>

      {/* Cash-flow preview card (mobile access point) */}
      <Link
        to="/cash-flow"
        className="flex items-center justify-between rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4 hover:bg-slate-50 transition-colors group"
        aria-label="Voir le cash-flow"
      >
        <div className="flex items-center gap-3">
          <LineChart size={20} className="text-slate-400 group-hover:text-slate-600 transition-colors" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-slate-800">Cash-flow</p>
            <p className="text-xs text-slate-500">
              {cfData
                ? `Solde prévisionnel M+12 : ${formatCurrency(cfProjectedNet)}`
                : 'Trajectoire et projections M+12'}
            </p>
          </div>
        </div>
        <ChevronRight size={16} className="text-slate-400 group-hover:text-slate-600 transition-colors" aria-hidden />
      </Link>

      {/* Graphique 12 mois */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-slate-800">Évolution 12 mois</h2>
          <PeriodSwitcher />
        </div>
        <MonthlyChart data={statsData ?? []} />
      </div>
    </div>
  );
}
